import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { carregarImagem, montarCartela } from '@/lib/cartela';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Chip from '@/components/ui/Chip';
import Campo from '@/components/ui/Campo';
import type { TalaoEncontrado } from '../types';

interface Props {
  talao: TalaoEncontrado;
  aoAvisar: (mensagem: string) => void;
}

type Estado = 'preparando' | 'pronto' | 'erro' | 'semArte';

// Um cartão de vidro por talão: nome, números, prévia da cartela e as ações.
// A prévia e o "Baixar" usam o mesmo montador do /meu-talao (@/lib/cartela).
export default function CartaoTalao({ talao, aoAvisar }: Props) {
  const [estado, setEstado] = useState<Estado>('preparando');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mandando, setMandando] = useState(false);

  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  // Monta a cartela (arte + QR + nome) quando o talão aparece.
  useEffect(() => {
    let ativo = true;
    setEstado('preparando');
    setBlob(null);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
      setBlobUrl(null);
    }

    (async () => {
      if (!talao.arte_url || !talao.link) {
        setEstado('semArte');
        return;
      }
      try {
        // Espera o QRCodeCanvas desenhar.
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        const qr = qrRef.current;
        if (!qr) throw new Error('QR não gerado.');

        const img = await carregarImagem(talao.arte_url);
        if (!ativo) return;

        const novo = await montarCartela(img, qr, {
          vendedor_id: 0,
          nome: talao.nome,
          token: talao.token,
          faixa: talao.faixa,
          link: talao.link,
          arte_url: talao.arte_url,
          caixa_qr: talao.caixa_qr,
          caixa_nome: talao.caixa_nome,
          evento: talao.evento,
          valor_cota_reais: talao.valor_cota_reais,
          arquivo: talao.arquivo,
          mensagem: talao.mensagem,
        });
        if (!ativo) return;

        urlRef.current = URL.createObjectURL(novo);
        setBlob(novo);
        setBlobUrl(urlRef.current);
        setEstado('pronto');
      } catch {
        if (ativo) setEstado('erro');
      }
    })();

    return () => {
      ativo = false;
    };
  }, [talao]);

  function baixar() {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = talao.arquivo || 'cartela.jpg';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function selecionarLink() {
    const el = document.getElementById(`link-talao-${talao.token}`) as HTMLInputElement | null;
    el?.focus();
    el?.select();
  }

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(talao.link);
      aoAvisar('Link copiado!');
    } catch {
      // Sem permissão de área de transferência: seleciona o link para copiar na mão.
      selecionarLink();
      aoAvisar('Selecione o link e copie.');
    }
  }

  async function copiarMensagem() {
    try {
      await navigator.clipboard.writeText(talao.mensagem);
      aoAvisar('Mensagem copiada!');
    } catch {
      aoAvisar('Não consegui copiar. Toque e segure o texto.');
    }
  }

  async function mandarWhats() {
    setMandando(true);
    try {
      if (blob) {
        const arquivo = new File([blob], talao.arquivo || 'cartela.jpg', {
          type: 'image/jpeg',
        });
        const podeCompartilhar =
          typeof navigator.canShare === 'function' && navigator.canShare({ files: [arquivo] });
        if (podeCompartilhar) {
          await navigator.share({ files: [arquivo], text: talao.mensagem });
          return;
        }
      }
      // Sem compartilhamento de arquivo: baixa a cartela e abre o WhatsApp com o texto.
      baixar();
      window.open(
        `https://wa.me/?text=${encodeURIComponent(talao.mensagem)}`,
        '_blank',
        'noopener,noreferrer',
      );
      aoAvisar('A cartela foi baixada. Anexe na conversa (o texto já está escrito).');
    } catch {
      /* a pessoa cancelou o compartilhamento */
    } finally {
      setMandando(false);
    }
  }

  const pronto = estado === 'pronto' && !!blobUrl;

  return (
    <div className="animate-subir">
      <Cartao variante="vidro" className="animate-pulso overflow-hidden p-5 ring-2 ring-success-400/70 md:p-6">
        {/* QR oculto, usado só para montar a cartela. */}
        {talao.link && (
          <div className="hidden" aria-hidden="true">
            <QRCodeCanvas
              ref={qrRef}
              value={talao.link}
              size={512}
              level="M"
              fgColor="#182878"
              bgColor="#ffffff"
            />
          </div>
        )}

        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[17px] font-bold uppercase leading-tight text-foreground-950">
            {talao.nome}
          </h2>
          <Chip tipo="success">Pronto para vender</Chip>
        </div>

        <p className="mt-3 text-[13px] font-semibold text-foreground-600">Seus números</p>
        <p className="text-[28px] font-extrabold leading-tight text-foreground-950">
          {talao.faixa}
        </p>

        {/* Prévia da cartela */}
        <div className="mt-4">
          {estado === 'preparando' && (
            <div className="h-40 w-full animate-pulse rounded-2xl bg-white/50" />
          )}

          {pronto && blobUrl && (
            <img
              src={blobUrl}
              alt={`Cartela de ${talao.nome} com o QR do talão`}
              title={`Cartela de ${talao.nome}`}
              className="w-full rounded-2xl border border-white/60 shadow-sm"
            />
          )}

          {(estado === 'erro' || estado === 'semArte') && (
            <div className="rounded-2xl border border-secondary-200 bg-background-50 p-4 text-center text-sm text-foreground-600">
              Não consegui montar a prévia agora.
            </div>
          )}
        </div>

        {/* Link do talão (somente leitura) */}
        <Campo
          id={`link-talao-${talao.token}`}
          rotulo="Link do talão"
          readOnly
          value={talao.link}
          className="mt-4"
          onFocus={(e) => e.currentTarget.select()}
        />

        {/* Ação principal */}
        <Botao
          tamanho="lg"
          icone="ri-whatsapp-line"
          carregando={mandando}
          className="mt-4 w-full"
          onClick={mandarWhats}
        >
          {mandando ? 'Abrindo…' : 'Mandar no WhatsApp'}
        </Botao>

        {/* Ações secundárias */}
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Botao
            variante="secundario"
            icone="ri-link"
            className="h-12 w-full"
            onClick={copiarLink}
          >
            Copiar link
          </Botao>
          <Botao
            variante="secundario"
            icone="ri-file-copy-line"
            className="h-12 w-full"
            onClick={copiarMensagem}
          >
            Copiar msg
          </Botao>
          <Botao
            variante="secundario"
            icone="ri-download-2-line"
            className="h-12 w-full"
            disabled={!pronto}
            onClick={baixar}
          >
            Baixar
          </Botao>
        </div>
      </Cartao>
    </div>
  );
}